//
// README - Beta Component
//
// This component is marked "beta" and will go through some iterations
// before landing at a final solution. Please reach out to the Cadence team
// if you intend to use this in the meantime and we'll help you out!
//

import React, { useRef } from 'react';
import classNames from 'classnames';

import { RCText } from '..';
import {
  RCModalBase,
  RCModalBaseRootProps,
  RCModalActionsProps,
  useRCModalBaseContext,
} from '../internalComponents/RCModalBase/RCModalBase';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';

/**
 * Closes the `RCDrawer` that contains the calling component.
 *
 * For content rendered inside a drawer that should dismiss it once its work is done — a navigation
 * link, an applied filter. Saves threading a close callback down through the drawer's children.
 *
 * Closing this way runs the same path as Escape or the close button, so the drawer's
 * `onOpenChange` still fires.
 *
 * Outside a drawer, `close` is a no-op — safe in a component rendered both inside and outside one.
 *
 * ```tsx
 * function NavLink({ href, children }) {
 *   const { close } = useRCDrawer();
 *   return <a href={href} onClick={close}>{children}</a>;
 * }
 * ```
 */
export function useRCDrawer() {
  return useRCModalBaseContext();
}

export interface RCDrawerProps {
  /** Optionally provide a trigger to have the drawer handle onClick behavior and state changes. */
  trigger?: JSX.Element;
  /** Can be used to control the state of the modal when paired with `onOpenChange`. */
  isOpen?: RCModalBaseRootProps['isOpen'];
  /** Can be used to control the state of the modal when paired with `isOpen`.
   *  Alternatively, can be used as a callback to track state changes.
  */
  onOpenChange?: RCModalBaseRootProps['onOpenChange'];
  /** The title of the drawer to be rendered in the header. */
  title: string;
  /** Actions to be appended at the bottom of the drawer, below the scrollable content area. */
  actions?: RCModalActionsProps;
  /** Defaults to `true`. Controls whether an overlay is rendered and whether outside clicks will close the drawer.*/
  isModal?: boolean;
  /** Defaults to `true`. Controls whether the drawer stays mounted to the DOM or is removed on close.*/
  keepMounted?: boolean;
  /** Defaults to `left`. Controls the position of the Drawer within the viewport. */
  position?: 'left' | 'right';
  /** Where focus lands when the drawer opens. Defaults to the close button. Pass a ref to
   *  an element inside the drawer to send focus there instead.
  */
  initialFocus?: RCModalBaseRootProps['initialFocus'];
}

/**
 * a toggled panel with standardized header & actions that overlays the viewport while staying attached to one side. At mobile widths, the drawer covers the entire viewport.
 *
 * Use a drawer in place of a modal if you have long sidebar content that you'd like to display without it taking up screen real estate (this component is essentially tailor-made for filter UI).
 */
export function RCDrawer({
  trigger = undefined,
  isOpen = undefined,
  onOpenChange = undefined,
  title = undefined,
  actions = undefined,
  isModal = true,
  children = undefined,
  keepMounted = true,
  position = 'left',
  initialFocus = undefined,
}: React.PropsWithChildren<RCDrawerProps>) {
  const closeButtonRef = useRef(null);

  return (
    <RCModalBase.Root
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      initialFocus={initialFocus ?? closeButtonRef}
      role="dialog"
      isModal={isModal}
      useDismissProps={isModal ? undefined : { enabled: false }}
    >
      {trigger && <RCModalBase.Trigger>{trigger}</RCModalBase.Trigger>}
      <RCModalBase.Content
        className="rc-drawer"
        position={position}
        displayStyle="drawer"
        keepMounted={keepMounted}
      >
        <div className="rc-drawer__header">
          <RCModalBase.Heading>
            <RCText.Title size="600">
              {title}
            </RCText.Title>
          </RCModalBase.Heading>
          <RCModalBase.CloseButton ref={closeButtonRef} />
        </div>
        <RCDrawerBody>{children}</RCDrawerBody>
        {actions &&
          <div className="rc-drawer__footer">
            <RCModalBase.Actions {...actions} />
          </div>
        }
      </RCModalBase.Content>
    </RCModalBase.Root>
  );
}

function RCDrawerBody({ children }) {
  const observerTop = useIntersectionObserver();
  const observerBottom = useIntersectionObserver();

  return (
    <div
      className={classNames(
        'rc-drawer__body',
        { 'rc-drawer__body--shadow-top': observerTop?.entry && !observerTop.entry.isIntersecting },
        { 'rc-drawer__body--shadow-bottom': observerBottom?.entry && !observerBottom.entry.isIntersecting },
      )}
    >
      <div className="rc-drawer__body__content">
        <div ref={observerTop?.setTrackedRef} data-observer />
        {children}
        <div ref={observerBottom?.setTrackedRef} data-observer />
      </div>
    </div>
  );
}
